import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  LockOutlined,
  Home,
  Logout,
  MailOutline,
  PersonOutline,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import Layout from '../components/Layout';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface HouseMember {
  id: string;
}

interface HouseSummary {
  id: string;
  host: HouseMember;
  coHosts?: HouseMember[];
}

interface InvitationSummary {
  id: string;
  status: 'pending' | 'accepted' | 'rejected';
}

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [houses, setHouses] = useState<HouseSummary[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  });
  const [accountFormData, setAccountFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [accountSaveSuccess, setAccountSaveSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, updateSession, updateUser } = useAuth();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [profileResponse, housesResponse, invitationsResponse] = await Promise.all([
          axios.get('/users/me'),
          axios.get('/houses'),
          axios.get('/invitations'),
        ]);

        setProfile(profileResponse.data);
        setFormData({
          firstName: profileResponse.data.firstName || '',
          lastName: profileResponse.data.lastName || '',
        });
        setAccountFormData({
          email: profileResponse.data.email || '',
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
        });
        setHouses(housesResponse.data);
        setPendingInvitations(
          (invitationsResponse.data as InvitationSummary[]).filter(invitation => invitation.status === 'pending').length,
        );
      } catch (err: any) {
        setError(err.response?.data?.message || t('profile.failedFetch'));
      } finally {
        setLoading(false);
      }
    };

    void fetchProfileData();
  }, [t]);

  const activeUser = profile || user;

  const stats = useMemo(() => {
    if (!activeUser) {
      return {
        hostedHomes: 0,
        visitingHomes: 0,
      };
    }

    const hostedHomes = houses.filter((house) => {
      if (house.host?.id === activeUser.id) {
        return true;
      }

      return Boolean(house.coHosts?.some(member => member.id === activeUser.id));
    }).length;

    return {
      hostedHomes,
      visitingHomes: Math.max(houses.length - hostedHomes, 0),
    };
  }, [activeUser, houses]);

  const initials = activeUser
    ? `${activeUser.firstName?.[0] || ''}${activeUser.lastName?.[0] || ''}`.toUpperCase()
    : '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(null);

      const response = await axios.patch('/users/me', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      });

      setProfile(response.data);
      updateUser(response.data);
      setSaveSuccess(t('profile.saveSuccess'));
    } catch (err: any) {
      const message = err?.response?.data?.message;
      if (Array.isArray(message)) {
        setError(message.join(', '));
      } else {
        setError(message || t('profile.failedSave'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccountSettings = async () => {
    if (accountFormData.newPassword && accountFormData.newPassword !== accountFormData.confirmNewPassword) {
      setAccountError(t('profile.passwordMismatch'));
      setAccountSaveSuccess(null);
      return;
    }

    try {
      setSavingAccount(true);
      setAccountError(null);
      setAccountSaveSuccess(null);

      const response = await axios.patch('/users/me/account', {
        email: accountFormData.email.trim(),
        currentPassword: accountFormData.currentPassword,
        newPassword: accountFormData.newPassword || undefined,
      });

      setProfile(response.data.user);
      updateSession(response.data);
      setAccountFormData(current => ({
        ...current,
        email: response.data.user.email,
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      }));
      setAccountSaveSuccess(t('profile.accountSaveSuccess'));
    } catch (err: any) {
      const message = err?.response?.data?.message;
      if (Array.isArray(message)) {
        setAccountError(message.join(', '));
      } else {
        setAccountError(message || t('profile.failedAccountSave'));
      }
    } finally {
      setSavingAccount(false);
    }
  };

  return (
    <Layout title={t('profile.layoutTitle')}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3">{t('profile.title')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
            {t('profile.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '1.4rem', fontWeight: 800 }}>
                      {initials || <PersonOutline />}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h4">
                        {activeUser ? `${activeUser.firstName} ${activeUser.lastName}` : t('profile.title')}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, color: 'text.secondary' }}>
                        <MailOutline fontSize="small" />
                        <Typography variant="body2" color="inherit">
                          {activeUser?.email}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('profile.signedInAs')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('common.fields.firstName')}
                        value={formData.firstName}
                        onChange={(event) => setFormData(current => ({ ...current, firstName: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('common.fields.lastName')}
                        value={formData.lastName}
                        onChange={(event) => setFormData(current => ({ ...current, lastName: event.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={saving || !formData.firstName.trim() || !formData.lastName.trim()}
                    >
                      {saving ? t('profile.saving') : t('common.actions.save')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setFormData({
                        firstName: activeUser?.firstName || '',
                        lastName: activeUser?.lastName || '',
                      })}
                      disabled={saving}
                    >
                      {t('profile.resetProfile')}
                    </Button>
                  </Stack>

                  {saveSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {saveSuccess}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <LockOutlined fontSize="small" color="primary" />
                    <Typography variant="h6">{t('profile.accountSecurityTitle')}</Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    label={t('common.fields.email')}
                    value={accountFormData.email}
                    onChange={(event) => setAccountFormData(current => ({ ...current, email: event.target.value }))}
                    sx={{ mb: 1.5 }}
                    type="email"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.currentPassword')}
                    value={accountFormData.currentPassword}
                    onChange={(event) => setAccountFormData(current => ({ ...current, currentPassword: event.target.value }))}
                    sx={{ mb: 1.5 }}
                    type="password"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.newPassword')}
                    value={accountFormData.newPassword}
                    onChange={(event) => setAccountFormData(current => ({ ...current, newPassword: event.target.value }))}
                    sx={{ mb: 1.5 }}
                    type="password"
                    helperText={t('profile.newPasswordHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('profile.confirmNewPassword')}
                    value={accountFormData.confirmNewPassword}
                    onChange={(event) => setAccountFormData(current => ({ ...current, confirmNewPassword: event.target.value }))}
                    type="password"
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveAccountSettings}
                      disabled={
                        savingAccount ||
                        !accountFormData.currentPassword.trim() ||
                        (
                          accountFormData.email.trim() === (activeUser?.email || '') &&
                          !accountFormData.newPassword.trim()
                        )
                      }
                    >
                      {savingAccount ? t('profile.saving') : t('profile.saveAccountSettings')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setAccountFormData({
                        email: activeUser?.email || '',
                        currentPassword: '',
                        newPassword: '',
                        confirmNewPassword: '',
                      })}
                      disabled={savingAccount}
                    >
                      {t('profile.resetProfile')}
                    </Button>
                  </Stack>

                  {accountError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {accountError}
                    </Alert>
                  )}

                  {accountSaveSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {accountSaveSuccess}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        {t('profile.hostedHomes')}
                      </Typography>
                      <Typography variant="h4">{stats.hostedHomes}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        {t('profile.visitingHomes')}
                      </Typography>
                      <Typography variant="h4">{stats.visitingHomes}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        {t('profile.pendingInvitations')}
                      </Typography>
                      <Typography variant="h4">{pendingInvitations}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Settings fontSize="small" color="primary" />
                    <Typography variant="h6">{t('profile.preferencesTitle')}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('profile.languageHelper')}
                  </Typography>
                  <LanguageSwitcher color="primary" />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {t('profile.sessionNote')}
                  </Typography>

                  <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
                    {t('profile.quickLinksTitle')}
                  </Typography>
                  <Stack spacing={1.25}>
                    <Button variant="outlined" startIcon={<Home />} onClick={() => navigate('/')}>
                      {t('profile.goToHouses')}
                    </Button>
                    <Button variant="outlined" startIcon={<MailOutline />} onClick={() => navigate('/invitations')}>
                      {t('profile.goToInvitations')}
                    </Button>
                    <Button variant="outlined" startIcon={<Logout />} onClick={handleLogout}>
                      {t('common.actions.logout')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Layout>
  );
};

export default ProfilePage;